import _ from 'lodash'
import React, {Component} from 'react'
import ContentEditable from 'react-contenteditable'
import {createValue} from '../../form/utils'
import CaretPositioning from './EditCaretPositioning'

/**
 * ContentEditableInput — rich text input with @mention support.
 *
 * Root-cause của cursor-jump / content-revert:
 *   react-contenteditable so sánh props.html với el.innerHTML trong componentDidUpdate.
 *   Nếu state.value chưa được cập nhật (debounce chưa fire), nó reset DOM về nội dung cũ.
 *
 * Giải pháp:
 *   - onChangeImmediate: fire NGAY trên mỗi input event → cập nhật state.value ngay lập tức
 *     → react-contenteditable luôn thấy props.html === el.innerHTML → KHÔNG reset DOM
 *   - debouncedNotifyParent: chỉ debounce việc gọi props.onChange (Formik)
 *     → tránh Formik re-render quá nhiều khi gõ phím liên tục
 *   - caretPosition: instance var (không phải state) → không trigger re-render khi cursor thay đổi
 *   - event.currentTarget null: dùng contentEditableInnerRef.current thay thế
 */
class ContentEditableInput extends Component {
    constructor(props) {
        super(props)
        const html = this._buildHtml(this.props.value ?? '')
        const initHtml = html.endsWith('>') ? html + ' ' : html

        this.state = {value: initHtml}

        // Instance var — cursor thay đổi KHÔNG trigger re-render
        this.caretPosition = {
            start: this._textOf(initHtml).length,
            end: this._textOf(initHtml).length,
        }
        // Lưu giá trị pending để debouncedNotifyParent dùng
        this._pendingValue = this.props.value ?? ''

        // Debounce chỉ cho việc notify parent (Formik) — tránh form re-render quá nhiều
        this.debouncedNotifyParent = _.debounce(this._notifyParent.bind(this), 500)

        this.contentEditableRef = React.createRef()
        this.contentEditableInnerRef = React.createRef()
    }

    // Public method — gọi qua ref từ MegaTextarea
    focus() {
        const el = this.contentEditableInnerRef.current
        if (!(el instanceof HTMLElement)) return
        requestAnimationFrame(() => el.focus())
    }

    shouldComponentUpdate(nextProps, nextState) {
        // Re-render chỉ khi value thực sự thay đổi
        // cursor move (caretPosition) KHÔNG trigger re-render vì là instance var
        if (nextProps.value !== this.props.value) return true
        if (nextState.value !== this.state.value) return true
        return false
    }

    componentDidUpdate(prevProps) {
        if (this.props.value === prevProps.value) return

        const html = this._buildHtml(this.props.value ?? '')
        const el = this.contentEditableInnerRef.current

        if (html === this.state.value) {
            // HTML không đổi (Formik roundtrip từ typing thường)
            // → KHÔNG setState → react-contenteditable KHÔNG reset DOM
            // → chỉ restore cursor để đảm bảo đúng vị trí
            if (el) CaretPositioning.restoreSelection(el, this.caretPosition)
            return
        }

        // HTML thay đổi (mention highlight, external/reset value)
        // → cập nhật, sau đó restore cursor trong callback
        this.setState({value: html}, () => {
            const innerEl = this.contentEditableInnerRef.current
            if (innerEl) CaretPositioning.restoreSelection(innerEl, this.caretPosition)
            this.props.onSelect({
                target: {
                    selectionStart: this.caretPosition?.start,
                    selectionEnd: this.caretPosition?.end,
                    value: this._textOf(html),
                },
            })
        })
    }

    // Encode spans → plain text với mention token  (@type:id:encodedName)
    // Chuyển <br> và block elements → \n để textContent preserve line breaks
    stripHTML = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html')

        // <br> → \n text node
        doc.body.querySelectorAll('br').forEach((br) => {
            br.parentNode.replaceChild(doc.createTextNode('\n'), br)
        })

        // Block element boundaries → \n (xử lý khi browser tạo <div> cho newline)
        doc.body.querySelectorAll('div, p').forEach((el) => {
            if (el.previousSibling) {
                el.parentNode.insertBefore(doc.createTextNode('\n'), el)
            }
        })

        // Encode mention spans → token string
        doc.body.querySelectorAll('span').forEach((span) => {
            span.textContent =
                (span.dataset.lexicalOwner ?? '') + encodeURI(span.textContent)
        })

        return doc.body.textContent
    }

    // Display text (không encode mention spans)
    _textOf = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        doc.body.querySelectorAll('br').forEach((br) => {
            br.parentNode.replaceChild(doc.createTextNode('\n'), br)
        })
        return doc.body.textContent
    }

    // Plain text (với mention tokens) → HTML (với styled spans)
    // Xử lý đúng \n trước/sau @mention (e.g. "\n@user:123:Name")
    _buildHtml = (text) => {
        if (!text) return ''
        return text
            .split(' ')
            .map((word) => {
                // Tách \n prefix để mention sau newline vẫn được highlight đúng
                const m = word.match(/^(\n*)(.*)$/)
                const nl = m ? m[1] : ''
                const w = m ? m[2] : word

                if (w.startsWith('@') && w.includes(':')) {
                    const parts = w.split(':')
                    const content = parts.pop()
                    const owner = parts.join(':') + ':'
                    return (
                        nl +
                        '<span data-lexical-text="true" data-lexical-owner="' +
                        owner +
                        '" style="font-weight:500;color:var(--color-primary)">' +
                        decodeURI(content) +
                        '</span>'
                    )
                }
                return word
            })
            .join(' ')
    }

    // Alias cho backward-compat
    transformText2Html = this._buildHtml
    stripHTMLWithoutEncode = this._textOf

    _notifyParent() {
        this.props.onChange(createValue(this.props.name, this._pendingValue))
    }

    // Fires IMMEDIATELY on each input event (NOT debounced)
    // Cập nhật state.value ngay → react-contenteditable không reset DOM
    onChangeImmediate() {
        const el = this.contentEditableInnerRef.current
        // Dùng element ref thay vì event.currentTarget
        // (event.currentTarget = null sau debounce vì DOM event cleared post-dispatch)
        if (!el || window.getSelection().rangeCount === 0) return

        this.caretPosition = CaretPositioning.saveSelection(el)
        const rawHtml = el.innerHTML || ''
        const newValue = this.stripHTML(rawHtml)
        const html = this._buildHtml(newValue)

        // Lưu để debouncedNotifyParent dùng
        this._pendingValue = newValue

        if (html !== this.state.value) {
            this.setState({value: html}, () => {
                // Restore cursor SAU KHI React có thể đã update DOM
                const innerEl = this.contentEditableInnerRef.current
                if (innerEl) CaretPositioning.restoreSelection(innerEl, this.caretPosition)
            })
        }

        // Chỉ notify Formik sau 500ms idle — tránh re-render form quá nhiều
        this.debouncedNotifyParent()
    }

    render() {
        const {
            name,
            className,
            onBlur,
            onKeyDown,
            onPaste,
            placeholder,
            style,
            rows,
        } = this.props

        return (
            <ContentEditable
                {...{name, className, onBlur, style, rows}}
                placeholder={placeholder}
                ref={this.contentEditableRef}
                innerRef={this.contentEditableInnerRef}
                html={this.state.value}
                onChange={() => this.onChangeImmediate()}
                onKeyDown={(event) => {
                    const textLen = this._textOf(this.state.value).length
                    if (event.key === ' ') {
                        // Trailing space sau mention span: thêm space để tránh browser merge
                        if (
                            textLen === this.caretPosition.start &&
                            this.state.value.endsWith('>')
                        ) {
                            this.caretPosition = {
                                start: this.caretPosition.start + 1,
                                end: this.caretPosition.end + 1,
                            }
                            this.setState({value: this.state.value + ' '})
                        }
                    } else {
                        onKeyDown(event, {
                            value:
                                this.props.value === this.stripHTML(this.state.value)
                                    ? this.props.value
                                    : null,
                        })
                    }
                }}
                onPaste={(event) => {
                    event.preventDefault()
                    onPaste(event)
                    const clipboardData = event.clipboardData || window.clipboardData
                    const pastedText = clipboardData.getData('text')
                    document.execCommand('insertText', false, pastedText)
                    // onChangeImmediate sẽ được trigger bởi input event từ execCommand
                }}
                tagName="div"
                role="textbox"
                style={{
                    border: 'none !important',
                    userSelect: 'text',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    width: '100%',
                    ...style,
                }}
            />
        )
    }
}

export default ContentEditableInput
